import { formatPeriodLabel, buildHeadersByPeriod } from "./dateRange";

// Top-N de series propias en el header de Ventas antes de colapsar el resto en
// "Otros". Igual a TOP_CLIENTS_COUNT del config de la vista; lo replicamos acá
// para no importar desde `views/` (cruzaría la capa Store → Views).
export const HEADER_CHART_TOP_N = 7;

const OTROS_LABEL = "Otros";

// Recorta la COLA de una serie: pone en `null` todo lo que viene DESPUÉS del
// último valor distinto de cero (los ceros/nulos finales no se dibujan). Así
// cada línea termina donde tuvo su último movimiento real, en vez de arrastrarse
// por el piso hasta el final del eje. Los ceros INTERMEDIOS se conservan (la
// línea sigue pasando por ellos). Una serie toda en cero/null → toda `null`.
export function cutTrailingZeros(values) {
    let last = -1;
    for (let i = values.length - 1; i >= 0; i -= 1) {
        const v = values[i];
        if (v != null && v !== 0) { last = i; break; }
    }
    return values.map((v, i) => (i > last ? null : v));
}

// Deriva las series `byClient` de los gráficos del header a partir de la data
// de la tabla (`accountsData` + `monthHeaders`), filtrando a `labels`. Replica
// la lógica del endpoint `charts` (top-N por acumulado + "Otros") pero del lado
// del cliente, para que el filtro multi-select del header reordene tabla y
// gráficos sin un refetch — y pueda mostrar cualquier item, no sólo los del
// top-N que devuelve el backend.
//
// Cada celda de `accountsData[label][header]` puede ser `{ value, prevYear }`
// (Ventas) o un número plano (Gastos) — `cellValue` soporta ambos para que el
// derive client-side del filtro multi-select sirva en los dos tabs.
// Período SIN data para esta etiqueta → `null` (no 0): en los charts de líneas
// `null` corta la línea (termina donde termina la data) en vez de arrastrarla
// por el piso hasta el final del eje; en los de barras no dibuja barra. Un 0
// REAL (celda presente que netea a cero) se conserva como 0.
function cellValue(cell) {
    if (cell == null) return null;
    return Number(typeof cell === "object" ? (cell.value ?? 0) : cell);
}

// `accountsData[label][header]` = `{ value, prevYear }` (Ventas) o número
// (Gastos). El shape de retorno matchea `normalizeSalesCharts` en lo que
// consume SalesChartsRow (`monthHeaders` + `byClient`).
export function deriveByClientSeries(accountsData, monthHeaders, labels, topN = HEADER_CHART_TOP_N) {
    const headers = monthHeaders || [];
    const series = (labels || []).map((label) => {
        const row = accountsData[label] || {};
        const values = headers.map((h) => cellValue(row[h]));
        const total = values.reduce((acc, v) => acc + (v || 0), 0);
        return { name: label, values, total };
    });

    // Ranking desc por acumulado; desempate por nombre asc para estabilidad
    // (mismo criterio que el backend: key=(-total, name)).
    series.sort((a, b) => (b.total - a.total) || a.name.localeCompare(b.name));

    const top = series.slice(0, topN);
    const rest = series.slice(topN);
    const hasOtros = rest.length > 0;

    // Recorte de cola por serie: cada línea termina tras su último valor ≠ 0.
    const byClient = top.map(({ name, values }) => ({ name, values: cutTrailingZeros(values) }));
    if (hasOtros) {
        // Otros = suma de las series-resto por bucket. `null` cuando NINGUNA
        // tiene data en ese bucket.
        const otrosValues = headers.map((_, i) => {
            let sum = 0;
            let any = false;
            rest.forEach((s) => {
                if (s.values[i] != null) { sum += s.values[i]; any = true; }
            });
            return any ? sum : null;
        });
        byClient.push({ name: OTROS_LABEL, values: cutTrailingZeros(otrosValues) });
    }

    return Object.freeze({
        monthHeaders: Object.freeze([...headers]),
        byClient: Object.freeze(byClient.map(Object.freeze)),
        hasOtros,
        topN,
    });
}

// Normaliza la respuesta cruda de `getSalesCharts` al shape que consumen los
// componentes de chart del header de Ventas (stacked + líneas) y el tab KPIs.
// El backend devuelve cada bucket como `{ period: ISO, total_amount|count|amount }`.
// Acá derivamos arrays paralelos alineados a `monthHeaders`, así los charts
// reciben data lista sin repetir el zipping.
//
// Centralizado porque lo usan dos módulos (salesReport y kpisReport) con el
// mismo contrato — cambiar el shape en un solo lugar.
export function normalizeSalesCharts(data, filterPeriod) {
    const {
        by_client: byClientRaw = [],
        client_counts: clientCountsRaw = [],
        avg_tickets: avgTicketsRaw = [],
        has_otros: hasOtros,
        top_n: topN,
        period_range: periodRange,
    } = data;

    // Eje X = TODOS los buckets del rango pedido, no sólo los que volvieron con
    // data. El backend agrupa y omite los períodos en cero, así que el chart
    // cortaba en el último mes con ventas; reconstruimos el rango completo desde
    // `period_range` (mismo `buildHeadersByPeriod` que usa la tabla → labels
    // idénticos) y alineamos cada serie por label, rellenando 0 donde falte.
    // Sin `period_range` (respuesta legacy) caemos al comportamiento anterior:
    // los períodos presentes en la data.
    const hasRange = periodRange && periodRange.init_date && periodRange.end_date;
    const monthHeaders = hasRange
        ? buildHeadersByPeriod(periodRange.init_date, periodRange.end_date, filterPeriod)
        : (byClientRaw[0]?.values || clientCountsRaw || [])
            .map((entry) => formatPeriodLabel(entry.period, filterPeriod));

    // Proyecta `entries` ({period, <valueKey>}) sobre `monthHeaders`: con rango
    // indexa por label y deja `null` los buckets ausentes (no 0): en los charts
    // de líneas eso corta la línea donde termina la data en vez de arrastrarla
    // por el piso hasta el final del eje; en barras no dibuja barra. Sin rango
    // respeta el orden que vino (comportamiento legacy).
    const project = (entries, valueKey) => {
        if (!hasRange) return entries.map((entry) => Number(entry[valueKey] || 0));
        const byLabel = {};
        entries.forEach((entry) => {
            byLabel[formatPeriodLabel(entry.period, filterPeriod)] = Number(entry[valueKey] || 0);
        });
        return monthHeaders.map((label) => (label in byLabel ? byLabel[label] : null));
    };

    // `cutTrailingZeros` recorta la cola de cada serie tras su último valor ≠ 0
    // — el backend rellena 0.0 en todos los buckets de la unión de períodos, así
    // que sin esto una serie que cae a 0 mientras otras siguen se arrastraba por
    // el piso. Con el recorte cada línea termina en su último movimiento real.
    const byClient = byClientRaw.map((series) => ({
        name: series.name,
        values: cutTrailingZeros(project(series.values, "total_amount")),
    }));
    const clientCounts = cutTrailingZeros(project(clientCountsRaw, "count"));
    const avgTickets = cutTrailingZeros(project(avgTicketsRaw, "amount"));

    return Object.freeze({
        monthHeaders: Object.freeze(monthHeaders),
        byClient: Object.freeze(byClient.map(Object.freeze)),
        clientCounts: Object.freeze(clientCounts),
        avgTickets: Object.freeze(avgTickets),
        hasOtros: Boolean(hasOtros),
        topN: Number(topN || 0),
        periodRange: Object.freeze(periodRange || {}),
    });
}
